import { Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationsService } from '@galaxy/atlas';
import { NotificationMedium, NotificationSetting } from '@vendasta/notifications-sdk';
import { NotificationContext } from '@vendasta/notifications-sdk/lib/_internal/objects/api';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, combineLatest, switchMap } from 'rxjs';
import { PARTNER_ID_TOKEN, USER_ID_TOKEN } from './tokens';

const INBOX_NOTIFICATION_TYPE_ID = 'inbox-messages';
const TURN_ON_NOTIFICATION_COOKIE = 'inbox-turn-on-notification';

@Injectable()
export class InboxNotificationService {
  readonly inboxNotificationStatus$ = combineLatest([this.partnerId$, this.userId$]).pipe(
    switchMap(([partnerId, userId]) => {
      return this.notificationsService.getNotificationSettingStatus$(
        partnerId,
        { user: { userId: userId } },
        INBOX_NOTIFICATION_TYPE_ID,
      );
    }),
  );

  private userCookiesValue$$ = new BehaviorSubject<string>('');

  constructor(
    private readonly notificationsService: NotificationsService,
    private readonly cookieService: CookieService,
    @Inject(USER_ID_TOKEN) readonly userId$: Observable<string>,
    @Inject(PARTNER_ID_TOKEN) readonly partnerId$: Observable<string>,
  ) {
    combineLatest([this.partnerId$, this.userId$])
      .pipe(takeUntilDestroyed())
      .subscribe(([partnerId, userId]) =>
        this.userCookiesValue$$.next(`${partnerId}-${userId}-${TURN_ON_NOTIFICATION_COOKIE}`),
      );
  }

  private getCookie(): string {
    return this.cookieService.get(TURN_ON_NOTIFICATION_COOKIE);
  }

  public getCookieStatus(): boolean {
    return this.getCookie() === this.userCookiesValue$$.getValue();
  }

  public setCookie(): void {
    const expiry = new Date();
    // we would like to make the warning banner disappear for 7 days after close
    expiry.setDate(expiry.getDate() + 6);
    this.cookieService.set(TURN_ON_NOTIFICATION_COOKIE, this.userCookiesValue$$.getValue(), expiry);
  }

  public turnOnOnlyMyEventsNotification$() {
    return combineLatest([this.userId$, this.partnerId$]).pipe(
      switchMap(([userId, partnerId]) => {
        const context = {
          user: {
            userId: userId,
          },
        } as NotificationContext;

        const emailSetting = {
          context: context,
          notificationTypeId: INBOX_NOTIFICATION_TYPE_ID,
          notificationMedium: NotificationMedium.NOTIFICATION_MEDIUM_EMAIL,
        } as NotificationSetting;

        const webSetting = {
          context: context,
          notificationTypeId: INBOX_NOTIFICATION_TYPE_ID,
          notificationMedium: NotificationMedium.NOTIFICATION_MEDIUM_WEB,
        } as NotificationSetting;

        return this.notificationsService.saveSettingMulti$(partnerId, [emailSetting, webSetting]);
      }),
    );
  }
}
